import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./styles.css";
import {
  filterAndSortCandidates,
  STATUS_META,
  type Candidate,
  type CandidateStatus,
  type PublicUser,
  type SortOrder,
} from "./domain";

interface ApiResponse {
  asOf: string;
  candidates: Candidate[];
}

interface SessionResponse { user: PublicUser | null; users: PublicUser[] }

type ViewName = "list" | "map";

const currency = new Intl.NumberFormat("da-DK", {
  style: "currency",
  currency: "DKK",
  maximumFractionDigits: 0,
});
const dateFormatter = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "short",
  year: "numeric",
});
const activityFormatter = new Intl.DateTimeFormat("da-DK", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const list = requiredElement<HTMLDivElement>("candidate-list");
const listView = requiredElement<HTMLElement>("list-view");
const mapView = requiredElement<HTMLElement>("map-view");
const topResultCount = requiredElement<HTMLElement>("top-result-count");
const mapResultCount = requiredElement<HTMLElement>("map-result-count");
const filterPanel = requiredElement<HTMLElement>("filter-panel");
const filterToggle = requiredElement<HTMLButtonElement>("filter-toggle");
const filterCount = requiredElement<HTMLElement>("filter-count");
const statusFilter = requiredElement<HTMLSelectElement>("status-filter");
const areaFilter = requiredElement<HTMLSelectElement>("area-filter");
const rentFilter = requiredElement<HTMLSelectElement>("rent-filter");
const roomsFilter = requiredElement<HTMLSelectElement>("rooms-filter");
const mediaFilter = requiredElement<HTMLSelectElement>("media-filter");
const heartFilter = requiredElement<HTMLSelectElement>("heart-filter");
const sortOrder = requiredElement<HTMLSelectElement>("sort-order");
const briefDialog = requiredElement<HTMLDialogElement>("brief-dialog");
const briefContent = requiredElement<HTMLDivElement>("brief-content");
const loginDialog = requiredElement<HTMLDialogElement>("login-dialog");
const loginForm = requiredElement<HTMLFormElement>("login-form");
const loginError = requiredElement<HTMLElement>("login-error");
const userMenu = requiredElement<HTMLButtonElement>("user-menu");
const userAvatar = requiredElement<HTMLElement>("user-avatar");
const userName = requiredElement<HTMLElement>("user-name");
const toast = requiredElement<HTMLElement>("toast");

const map = L.map("map", {
  scrollWheelZoom: true,
  zoomControl: true,
}).setView([55.94, 12.45], 10);

L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
  maxZoom: 19,
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
}).addTo(map);

const markerLayer = L.featureGroup().addTo(map);
let candidates: Candidate[] = [];
let visibleCandidates: Candidate[] = [];
let currentView: ViewName = "list";
let currentUser: PublicUser | null = null;
let currentBriefId: string | null = null;

document.querySelectorAll<HTMLButtonElement>("[data-view]").forEach((button) => {
  button.addEventListener("click", () => setView(button.dataset.view as ViewName));
});

[statusFilter, areaFilter, rentFilter, roomsFilter, mediaFilter, heartFilter, sortOrder].forEach(
  (control) => control.addEventListener("change", render),
);

filterToggle.addEventListener("click", () => setFilterPanel(filterPanel.hidden));
requiredElement<HTMLButtonElement>("filter-close").addEventListener("click", () => setFilterPanel(false));
requiredElement<HTMLButtonElement>("filter-apply").addEventListener("click", () => setFilterPanel(false));
requiredElement<HTMLButtonElement>("filter-reset").addEventListener("click", resetFilters);

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && !filterPanel.hidden) setFilterPanel(false);
});

document.addEventListener("click", (event) => {
  const target = event.target as Node;
  if (!filterPanel.hidden && !filterPanel.contains(target) && !filterToggle.contains(target)) {
    setFilterPanel(false);
  }
});

document.addEventListener(
  "error",
  (event) => {
    const target = event.target;
    if (target instanceof HTMLImageElement && target.dataset.remoteImage !== undefined) {
      target.closest<HTMLElement>(".media-frame")?.classList.add("has-image-error");
    }
  },
  true,
);

briefDialog.addEventListener("click", (event) => {
  if (event.target === briefDialog) closeBrief();
});
briefDialog.addEventListener("close", () => { currentBriefId = null; });
loginDialog.addEventListener("cancel", (event) => event.preventDefault());
loginForm.addEventListener("submit", (event) => { event.preventDefault(); void login(); });
userMenu.addEventListener("click", () => void logout());

void initialise();

async function initialise(): Promise<void> {
  try {
    const response = await fetch("/api/session");
    const session = (await response.json()) as SessionResponse;
    if (session.user) {
      setCurrentUser(session.user);
      await load();
    } else {
      showLogin();
    }
  } catch (error) {
    console.error(error);
    renderError();
  }
}

async function load(): Promise<void> {
  try {
    const response = await fetch("/api/candidates");
    if (response.status === 401) { showLogin(); return; }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = (await response.json()) as ApiResponse;
    candidates = data.candidates;
    render();
  } catch (error) {
    console.error(error);
    renderError();
  }
}

function render(): void {
  const maxRent = rentFilter.value === "all" ? Number.POSITIVE_INFINITY : Number(rentFilter.value);
  const minRooms = Number(roomsFilter.value);

  visibleCandidates = filterAndSortCandidates(
    candidates,
    statusFilter.value as CandidateStatus | "all",
    sortOrder.value as SortOrder,
  ).filter((candidate) => {
    const areaMatches = areaFilter.value === "all" || candidate.city === areaFilter.value;
    const mediaMatches =
      mediaFilter.value === "all" ||
      (mediaFilter.value === "floorplan" && candidate.brief.floorPlans.length > 0) ||
      (mediaFilter.value === "photos" && candidate.brief.photos.length > 0);
    const hearts = candidate.engagement.hearts;
    const heartMatches = heartFilter.value === "all" ||
      (heartFilter.value === "any" && hearts.length > 0) ||
      (heartFilter.value === "none" && hearts.length === 0) ||
      (heartFilter.value === "mine" && !!currentUser && hearts.some((user) => user.username === currentUser?.username)) ||
      hearts.some((user) => user.username === heartFilter.value);
    return areaMatches && candidate.rent <= maxRent && candidate.rooms >= minRooms && mediaMatches && heartMatches;
  });

  const resultLabel = `${visibleCandidates.length} ${visibleCandidates.length === 1 ? "bolig" : "boliger"}`;
  topResultCount.textContent = resultLabel;
  mapResultCount.textContent = resultLabel;
  renderFilterCount();
  renderList();
  renderMap();
}

function renderList(): void {
  if (visibleCandidates.length === 0) {
    list.innerHTML = `
      <div class="state-card" role="status">
        <strong>Ingen kandidater matcher filtrene.</strong>
        <span>Nulstil filtrene, eller udvid pris og område.</span>
        <button type="button" class="secondary-button" data-reset-empty>Nulstil filtre</button>
      </div>`;
    list.querySelector<HTMLButtonElement>("[data-reset-empty]")?.addEventListener("click", resetFilters);
    return;
  }

  list.replaceChildren(
    ...visibleCandidates.map((candidate, index) => createCandidateRow(candidate, index + 1)),
  );
}

function createCandidateRow(candidate: Candidate, rank: number): HTMLElement {
  const meta = STATUS_META[candidate.status];
  const photo = candidate.brief.photos[0];
  const row = document.createElement("article");
  row.className = `candidate-row${candidate.status === "udlejet" ? " is-archived" : ""}`;
  row.innerHTML = `
    <div class="candidate-image media-frame">
      <span class="media-fallback">Intet verificeret billede</span>
      ${photo ? `<img data-remote-image loading="lazy" src="${safeUrl(photo.src)}" alt="${escapeHtml(photo.alt)}">` : ""}
      <span class="rank">${rank}</span>
    </div>
    <div class="candidate-main">
      <div class="candidate-heading">
        <div>
          <span class="status-badge status-${meta.tone}">${meta.label}</span>
          <h2>${escapeHtml(candidate.address)}</h2>
          <p>${escapeHtml(candidate.postalCode)} ${escapeHtml(candidate.city)} · ${escapeHtml(candidate.propertyType)}</p>
        </div>
        <strong class="price">${currency.format(candidate.rent)}<small>/md.</small></strong>
      </div>
      <p class="candidate-summary">${escapeHtml(candidate.brief.summary)}</p>
      <div class="social-summary">${heartSummary(candidate)}<span>${candidate.engagement.comments.length} ${candidate.engagement.comments.length === 1 ? "kommentar" : "kommentarer"}</span></div>
      <dl class="row-facts">
        <div><dt>Værelser</dt><dd>${candidate.rooms}</dd></div>
        <div><dt>Areal</dt><dd>${candidate.areaM2} m²</dd></div>
        <div><dt>Ledig</dt><dd>${formatDate(candidate.availableFrom)}</dd></div>
        <div><dt>Materiale</dt><dd>${mediaLabel(candidate)}</dd></div>
      </dl>
    </div>
    <div class="candidate-actions">
      ${candidate.risks[0] ? `<p><strong>Vær opmærksom</strong>${escapeHtml(candidate.risks[0])}</p>` : ""}
      <button class="heart-button${hasMyHeart(candidate) ? " is-active" : ""}" type="button" data-heart-id="${escapeHtml(candidate.id)}" aria-pressed="${hasMyHeart(candidate)}">♥ <span>${hasMyHeart(candidate) ? "Dit hjerte" : "Giv hjerte"}</span></button>
      <button class="primary-button" type="button" data-brief-id="${escapeHtml(candidate.id)}">Åbn brief</button>
      <a class="secondary-link" href="${safeUrl(candidate.url)}" target="_blank" rel="noreferrer">Original annonce</a>
    </div>`;

  row.querySelector<HTMLButtonElement>("[data-brief-id]")?.addEventListener("click", () => openBrief(candidate));
  row.querySelector<HTMLButtonElement>("[data-heart-id]")?.addEventListener("click", (event) => void toggleHeart(candidate.id, event.currentTarget as HTMLButtonElement));
  return row;
}

function renderMap(): void {
  markerLayer.clearLayers();

  visibleCandidates.forEach((candidate, index) => {
    const meta = STATUS_META[candidate.status];
    const photo = candidate.brief.photos[0];
    const marker = L.marker([candidate.location.lat, candidate.location.lng], {
      title: `${candidate.address}, ${candidate.city}`,
      alt: `${candidate.address}, ${candidate.city}. ${meta.label}`,
      keyboard: true,
      icon: L.divIcon({
        className: "candidate-marker-shell",
        html: `<span class="candidate-marker marker-${meta.tone}">${index + 1}</span>`,
        iconSize: [38, 38],
        iconAnchor: [19, 19],
        tooltipAnchor: [0, -18],
      }),
    });

    marker.bindTooltip(
      `<div class="marker-preview">
        ${photo ? `<img src="${safeUrl(photo.src)}" alt="">` : '<div class="marker-image-empty">Intet billede</div>'}
        <div><span class="tooltip-status">${meta.label}</span><strong>${escapeHtml(candidate.address)}</strong><span>${candidate.rooms} vær. · ${candidate.areaM2} m²</span><b>${currency.format(candidate.rent)}/md.</b><small>♥ ${candidate.engagement.hearts.length}${candidate.engagement.hearts.length ? ` · ${escapeHtml(candidate.engagement.hearts.map((user) => user.displayName).join(", "))}` : ""}</small><small>Klik for brief</small></div>
      </div>`,
      { direction: "top", opacity: 1, className: "candidate-tooltip", sticky: true },
    );
    marker.on("click", () => openBrief(candidate));
    marker.on("focus", () => marker.openTooltip());
    marker.on("blur", () => marker.closeTooltip());
    marker.addTo(markerLayer);
  });

  fitMap();
}

function fitMap(): void {
  const bounds = markerLayer.getBounds();
  if (bounds.isValid()) map.fitBounds(bounds, { padding: [70, 70], maxZoom: 14 });
}

function setView(view: ViewName): void {
  currentView = view;
  const showMap = view === "map";
  listView.hidden = showMap;
  mapView.hidden = !showMap;
  document.body.classList.toggle("map-active", showMap);

  document.querySelectorAll<HTMLButtonElement>("[data-view]").forEach((button) => {
    const active = button.dataset.view === view;
    button.classList.toggle("is-active", active);
    button.setAttribute("aria-pressed", String(active));
  });

  setFilterPanel(false);
  if (showMap) {
    requestAnimationFrame(() => {
      map.invalidateSize();
      fitMap();
    });
  }
}

function setFilterPanel(open: boolean): void {
  filterPanel.hidden = !open;
  filterToggle.setAttribute("aria-expanded", String(open));
  if (open) statusFilter.focus();
}

function resetFilters(): void {
  statusFilter.value = "all";
  areaFilter.value = "all";
  rentFilter.value = "all";
  roomsFilter.value = "5";
  mediaFilter.value = "all";
  heartFilter.value = "all";
  sortOrder.value = "priority";
  render();
  setFilterPanel(false);
}

function renderFilterCount(): void {
  const count = [
    statusFilter.value !== "all",
    areaFilter.value !== "all",
    rentFilter.value !== "all",
    roomsFilter.value !== "5",
    mediaFilter.value !== "all",
    heartFilter.value !== "all",
  ].filter(Boolean).length;
  filterCount.textContent = String(count);
  filterCount.hidden = count === 0;
}

function openBrief(candidate: Candidate): void {
  currentBriefId = candidate.id;
  const meta = STATUS_META[candidate.status];
  const brief = candidate.brief;
  const photos = brief.photos.length
    ? brief.photos.map((item) => galleryItem(item.src, item.alt)).join("")
    : '<div class="brief-empty-media"><strong>Ingen verificerede billeder</strong><span>Det oprindelige galleri er ikke længere tilgængeligt.</span></div>';
  const floorPlans = brief.floorPlans.length
    ? brief.floorPlans.map((item) => galleryItem(item.src, item.alt, true)).join("")
    : '<p class="brief-missing">Ingen verificeret plantegning fundet.</p>';

  briefContent.innerHTML = `
    <header class="brief-header">
      <div>
        <span class="status-badge status-${meta.tone}">${meta.label}</span>
        <h2 id="brief-title">${escapeHtml(candidate.address)}</h2>
        <p>${escapeHtml(candidate.postalCode)} ${escapeHtml(candidate.city)} · kontrolleret ${formatDate(brief.checkedAt)}</p>
      </div>
      <button class="icon-button brief-close" type="button" aria-label="Luk brief">×</button>
    </header>
    <div class="brief-body">
      <section class="brief-intro">
        <div>
          <p class="brief-lead">${escapeHtml(brief.summary)}</p>
          <p>${escapeHtml(brief.description)}</p>
        </div>
        <div class="brief-price"><span>Husleje</span><strong>${currency.format(candidate.rent)}</strong><small>${candidate.monthlyTotal ? `${currency.format(candidate.monthlyTotal)} inkl. kendt aconto` : "Forbrug ikke fuldt afklaret"}</small></div>
      </section>

      <dl class="brief-key-facts">
        <div><dt>Værelser</dt><dd>${candidate.rooms}</dd></div>
        <div><dt>Areal</dt><dd>${candidate.areaM2} m²</dd></div>
        <div><dt>Indflytning</dt><dd>${formatDate(candidate.availableFrom)}</dd></div>
        <div><dt>Indflytningspris</dt><dd>${candidate.moveInTotal ? currency.format(candidate.moveInTotal) : "Ikke oplyst"}</dd></div>
      </dl>

      <section class="brief-section collaboration-section">
        <div class="collaboration-heading"><div><p class="eyebrow">Familiens vurdering</p><h3>Hjerter og kommentarer</h3></div><button class="heart-button large${hasMyHeart(candidate) ? " is-active" : ""}" type="button" data-brief-heart aria-pressed="${hasMyHeart(candidate)}">♥ ${hasMyHeart(candidate) ? "Du har givet et hjerte" : "Giv et hjerte"}</button></div>
        <div class="heart-people">${candidate.engagement.hearts.length ? candidate.engagement.hearts.map(userChip).join("") : '<span class="empty-inline">Ingen hjerter endnu</span>'}</div>
        <div class="comment-feed">${candidate.engagement.comments.length ? [...candidate.engagement.comments].reverse().map(commentItem).join("") : '<p class="empty-comments">Ingen kommentarer endnu. Skriv det første indtryk.</p>'}</div>
        <form class="comment-form" data-comment-form>
          <label for="comment-body">Din kommentar som ${escapeHtml(currentUser?.displayName ?? "bruger")}</label>
          <div><textarea id="comment-body" name="body" rows="2" maxlength="800" placeholder="Fx: God planløsning, men vi bør tjekke transporten…" required></textarea><button class="primary-button" type="submit">Send</button></div>
        </form>
      </section>

      <section class="brief-section"><div class="section-heading"><h3>Billeder</h3><span>${brief.photos.length} stk.</span></div><p class="media-caveat">${escapeHtml(brief.mediaCaveat)}</p><div class="brief-gallery">${photos}</div></section>
      <section class="brief-section"><div class="section-heading"><h3>Plantegning</h3><span>${brief.floorPlans.length ? "Verificeret" : "Mangler"}</span></div><div class="floorplan-gallery">${floorPlans}</div></section>

      <div class="brief-columns">
        <section class="brief-section"><h3>Det ved vi</h3><ul class="check-list">${brief.facts.map((fact) => `<li>${escapeHtml(fact)}</li>`).join("")}</ul></section>
        <section class="brief-section brief-considerations"><h3>Skal afklares</h3><ul>${[...brief.considerations, ...candidate.risks].map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul></section>
      </div>

      <section class="brief-section source-section">
        <div><h3>Kilde og næste skridt</h3><p>${escapeHtml(brief.sourceNote)}</p><p><strong>Næste handling:</strong> ${escapeHtml(candidate.nextAction)}</p>${brief.contact ? `<p><strong>Kontakt:</strong> ${escapeHtml(brief.contact.name)}${brief.contact.phone ? ` · ${escapeHtml(brief.contact.phone)}` : ""}${brief.contact.email ? ` · <a href="mailto:${escapeHtml(brief.contact.email)}">${escapeHtml(brief.contact.email)}</a>` : ""}</p>` : ""}</div>
        <a class="primary-button" href="${safeUrl(candidate.url)}" target="_blank" rel="noreferrer">Åbn original annonce</a>
      </section>
    </div>`;

  briefContent.querySelector<HTMLButtonElement>(".brief-close")?.addEventListener("click", closeBrief);
  briefContent.querySelector<HTMLButtonElement>("[data-brief-heart]")?.addEventListener("click", (event) => void toggleHeart(candidate.id, event.currentTarget as HTMLButtonElement));
  briefContent.querySelector<HTMLFormElement>("[data-comment-form]")?.addEventListener("submit", (event) => { event.preventDefault(); void addComment(candidate.id, event.currentTarget as HTMLFormElement); });
  briefContent.querySelectorAll<HTMLButtonElement>("[data-delete-comment]").forEach((button) => button.addEventListener("click", () => void deleteComment(button.dataset.deleteComment ?? "")));
  if (!briefDialog.open) briefDialog.showModal();
}

function closeBrief(): void {
  currentBriefId = null;
  briefDialog.close();
}

function hasMyHeart(candidate: Candidate): boolean {
  return !!currentUser && candidate.engagement.hearts.some((user) => user.username === currentUser?.username);
}

function userChip(user: PublicUser): string {
  return `<span class="person-chip"><i style="--person-color:${escapeHtml(user.color)}">${escapeHtml(user.displayName.slice(0, 1))}</i>${escapeHtml(user.displayName)}</span>`;
}

function heartSummary(candidate: Candidate): string {
  if (!candidate.engagement.hearts.length) return '<span class="heart-summary is-empty">♡ Ingen hjerter endnu</span>';
  return `<span class="heart-summary">♥ ${candidate.engagement.hearts.length} · ${escapeHtml(candidate.engagement.hearts.map((user) => user.displayName).join(", "))}</span>`;
}

function commentItem(comment: Candidate["engagement"]["comments"][number]): string {
  return `<article class="comment-item">
    <div class="comment-avatar" style="--person-color:${escapeHtml(comment.user.color)}">${escapeHtml(comment.user.displayName.slice(0, 1))}</div>
    <div><header><strong>${escapeHtml(comment.user.displayName)}</strong><time datetime="${escapeHtml(comment.createdAt)}">${activityFormatter.format(new Date(comment.createdAt))}</time>${comment.canDelete ? `<button type="button" data-delete-comment="${escapeHtml(comment.id)}">Slet</button>` : ""}</header><p>${escapeHtml(comment.body)}</p></div>
  </article>`;
}

async function toggleHeart(candidateId: string, button: HTMLButtonElement): Promise<void> {
  button.disabled = true;
  try {
    const response = await fetch(`/api/candidates/${encodeURIComponent(candidateId)}/heart`, { method: "POST" });
    if (response.status === 401) { showLogin(); return; }
    if (!response.ok) throw new Error(await responseMessage(response));
    await refreshAfterInteraction(candidateId);
  } catch (error) {
    showToast(error instanceof Error ? error.message : "Hjertet kunne ikke gemmes.", true);
  } finally {
    button.disabled = false;
  }
}

async function addComment(candidateId: string, form: HTMLFormElement): Promise<void> {
  const textarea = form.elements.namedItem("body") as HTMLTextAreaElement;
  const submit = form.querySelector<HTMLButtonElement>("button[type=submit]");
  if (!textarea.value.trim()) return;
  if (submit) submit.disabled = true;
  try {
    const response = await fetch(`/api/candidates/${encodeURIComponent(candidateId)}/comments`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ body: textarea.value }),
    });
    if (!response.ok) throw new Error(await responseMessage(response));
    textarea.value = "";
    await refreshAfterInteraction(candidateId);
    showToast("Kommentaren er gemt.");
  } catch (error) {
    showToast(error instanceof Error ? error.message : "Kommentaren kunne ikke gemmes.", true);
  } finally {
    if (submit) submit.disabled = false;
  }
}

async function deleteComment(commentId: string): Promise<void> {
  if (!commentId || !window.confirm("Slet din kommentar?")) return;
  try {
    const response = await fetch(`/api/comments/${encodeURIComponent(commentId)}`, { method: "DELETE" });
    if (!response.ok) throw new Error(await responseMessage(response));
    const candidateId = currentBriefId;
    if (candidateId) await refreshAfterInteraction(candidateId);
    showToast("Kommentaren er slettet.");
  } catch (error) {
    showToast(error instanceof Error ? error.message : "Kommentaren kunne ikke slettes.", true);
  }
}

async function refreshAfterInteraction(candidateId: string): Promise<void> {
  const response = await fetch("/api/candidates");
  if (!response.ok) throw new Error(await responseMessage(response));
  candidates = ((await response.json()) as ApiResponse).candidates;
  render();
  if (currentBriefId === candidateId) {
    const updated = candidates.find((candidate) => candidate.id === candidateId);
    if (updated) openBrief(updated);
  }
}

async function login(): Promise<void> {
  const submit = loginForm.querySelector<HTMLButtonElement>("button[type=submit]");
  const form = new FormData(loginForm);
  loginError.hidden = true;
  if (submit) submit.disabled = true;
  try {
    const response = await fetch("/api/login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ username: form.get("username"), password: form.get("password") }),
    });
    if (!response.ok) throw new Error(await responseMessage(response));
    const { user } = await response.json() as { user: PublicUser };
    setCurrentUser(user);
    loginDialog.close();
    loginForm.reset();
    await load();
  } catch (error) {
    loginError.textContent = error instanceof Error ? error.message : "Login mislykkedes.";
    loginError.hidden = false;
  } finally {
    if (submit) submit.disabled = false;
  }
}

async function logout(): Promise<void> {
  await fetch("/api/logout", { method: "POST" });
  currentUser = null;
  candidates = [];
  userMenu.hidden = true;
  if (briefDialog.open) closeBrief();
  showLogin();
}

function setCurrentUser(user: PublicUser): void {
  currentUser = user;
  userName.textContent = user.displayName;
  userAvatar.textContent = user.displayName.slice(0, 1);
  userAvatar.style.setProperty("--person-color", user.color);
  userMenu.hidden = false;
}

function showLogin(): void {
  if (!loginDialog.open) loginDialog.showModal();
  requestAnimationFrame(() => requiredElement<HTMLSelectElement>("login-user").focus());
}

async function responseMessage(response: Response): Promise<string> {
  try { return ((await response.json()) as { message?: string }).message ?? `HTTP ${response.status}`; }
  catch { return `HTTP ${response.status}`; }
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;
function showToast(message: string, error = false): void {
  toast.textContent = message;
  toast.classList.toggle("is-error", error);
  toast.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.hidden = true; }, 3200);
}

function galleryItem(src: string, alt: string, floorPlan = false): string {
  return `<a class="gallery-item media-frame${floorPlan ? " is-floorplan" : ""}" href="${safeUrl(src)}" target="_blank" rel="noreferrer">
    <span class="media-fallback">Billedet kunne ikke indlæses</span>
    <img data-remote-image loading="lazy" src="${safeUrl(src)}" alt="${escapeHtml(alt)}">
  </a>`;
}

function mediaLabel(candidate: Candidate): string {
  const parts = [];
  if (candidate.brief.photos.length) parts.push(`${candidate.brief.photos.length} fotos`);
  if (candidate.brief.floorPlans.length) parts.push("plantegning");
  return parts.join(" + ") || "ingen billeder";
}

function renderError(): void {
  topResultCount.textContent = "Datafejl";
  mapResultCount.textContent = "Datafejl";
  list.innerHTML = `<div class="state-card" role="alert"><strong>Boligerne kunne ikke hentes.</strong><span>Kontrollér at Bun-serveren kører, og genindlæs siden.</span><button class="primary-button" type="button" data-retry>Prøv igen</button></div>`;
  list.querySelector<HTMLButtonElement>("[data-retry]")?.addEventListener("click", () => void load());
}

function formatDate(value: string): string {
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date);
}

function requiredElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Mangler element #${id}`);
  return element as T;
}

function safeUrl(value: string): string {
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:" ? escapeHtml(url.href) : "#";
  } catch {
    return "#";
  }
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#039;");
}
